/**
 * Canonical paper lifecycle (spec §3). Internal statuses are normalized;
 * agencies may relabel them (WF-001) via `statusLabels` in agency settings.
 *
 * RECEIVED → INTAKE → REVIEWED → READY (payment complete / exempt) → ASSIGNED
 * → IN_SERVICE (attempts) → SERVED | NON_SERVED | PENDING_REVIEW
 * → AFFIDAVIT_SIGNED → RETURNED → CLOSED → ARCHIVED
 *
 * On-hold and recalled are flags, not statuses, so they never lose position.
 */
import type { PaperPhase } from './rules/engine.ts';

export const PAPER_STATUSES = [
  'RECEIVED',
  'INTAKE',
  'REVIEWED',
  'READY',
  'ASSIGNED',
  'IN_SERVICE',
  'PENDING_REVIEW',
  'SERVED',
  'NON_SERVED',
  'AFFIDAVIT_SIGNED',
  'RETURNED',
  'CLOSED',
  'ARCHIVED',
] as const;
export type PaperStatus = (typeof PAPER_STATUSES)[number];

export const DEFAULT_STATUS_LABELS: Record<PaperStatus, string> = {
  RECEIVED: 'Received',
  INTAKE: 'Intake',
  REVIEWED: 'Reviewed',
  READY: 'Ready',
  ASSIGNED: 'Assigned',
  IN_SERVICE: 'Attempting',
  PENDING_REVIEW: 'Supervisor review',
  SERVED: 'Served',
  NON_SERVED: 'Non-served',
  AFFIDAVIT_SIGNED: 'Affidavit signed',
  RETURNED: 'Returned',
  CLOSED: 'Closed',
  ARCHIVED: 'Archived',
};

const TRANSITIONS: Record<PaperStatus, PaperStatus[]> = {
  RECEIVED: ['INTAKE', 'ARCHIVED'],
  // → RETURNED only for a paper sent to another sheriff's office, once its return is received.
  INTAKE: ['REVIEWED', 'ARCHIVED', 'RETURNED'],
  REVIEWED: ['READY', 'ARCHIVED', 'RETURNED'],
  READY: ['ASSIGNED', 'ARCHIVED', 'RETURNED'],
  ASSIGNED: ['READY', 'IN_SERVICE', 'SERVED', 'NON_SERVED', 'PENDING_REVIEW'],
  IN_SERVICE: ['READY', 'ASSIGNED', 'SERVED', 'NON_SERVED', 'PENDING_REVIEW'],
  PENDING_REVIEW: ['IN_SERVICE', 'ASSIGNED', 'NON_SERVED', 'SERVED'],
  SERVED: ['AFFIDAVIT_SIGNED'],
  NON_SERVED: ['AFFIDAVIT_SIGNED'],
  AFFIDAVIT_SIGNED: ['RETURNED', 'CLOSED'],
  RETURNED: ['CLOSED'],
  CLOSED: ['ARCHIVED'],
  ARCHIVED: [],
};

export function canTransition(from: PaperStatus, to: PaperStatus): boolean {
  return TRANSITIONS[from].includes(to);
}

export class TransitionError extends Error {
  constructor(
    public from: PaperStatus,
    public to: PaperStatus,
  ) {
    super(`Invalid status transition ${from} → ${to}`);
  }
}

export function assertTransition(from: PaperStatus, to: PaperStatus): void {
  if (!canTransition(from, to)) throw new TransitionError(from, to);
}

/** Map lifecycle status onto the rules engine's coarse phase. */
export function phaseOf(status: PaperStatus): PaperPhase {
  switch (status) {
    case 'SERVED':
      return 'served';
    case 'NON_SERVED':
      return 'non_served';
    case 'PENDING_REVIEW':
      return 'pending_review';
    case 'AFFIDAVIT_SIGNED':
    case 'RETURNED':
    case 'CLOSED':
    case 'ARCHIVED':
      return 'closed';
    default:
      return 'open';
  }
}

/** Statuses in which a server may record in-person attempts. */
export const ATTEMPTABLE_STATUSES: PaperStatus[] = ['ASSIGNED', 'IN_SERVICE'];

/** Customer-facing simple timeline (PORT-004). */
export function customerTimelineStep(status: PaperStatus, attemptCount: number): 'Received' | 'Assigned' | 'Attempted' | 'Served' | 'Non-Served' {
  if (['SERVED'].includes(status)) return 'Served';
  if (['NON_SERVED'].includes(status)) return 'Non-Served';
  if (attemptCount > 0) return 'Attempted';
  if (['ASSIGNED', 'IN_SERVICE', 'PENDING_REVIEW'].includes(status)) return 'Assigned';
  return 'Received';
}

export type PaymentStatus = 'PENDING' | 'COMPLETE' | 'EXEMPT' | 'OVERRIDDEN';
